import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { FindLoad, Images, renderStaffName } from 'utils';
import { ErrMessage, TextRow, InputMinLoader } from 'components';
import { Popper } from '@mui/material';
import { CredentialMatchMode } from '../../../fundingSource/fundingSourceSingle/core/constants';

const GET_CLIENT_STAFFS = 'GET_CLIENT_STAFFS';

/** "BCBA, BCaBA" → "No staff holds all required credentials: BCBA, BCaBA" style reason. */
const renderNoStaffReason = (credentialMatch, requiredCredentials = []) => {
    if (!requiredCredentials.length) return 'No credentials are set on this charge rate.';
    const which = credentialMatch === CredentialMatchMode.ANY ? 'any of the' : 'all';
    return `No staff holds ${which} required credentials: ${requiredCredentials.join(', ')}`;
};

/** Why the staff list is empty, one line per charge rate on the service (from GET /appt/staffs/suggestion). */
const buildNoStaffReasons = (clientStaffs) => {
    const showName = clientStaffs.length > 1;
    return clientStaffs.map((item) => {
        const reason = renderNoStaffReason(item?.credentialMatch, item?.requiredCredentials);
        return showName && item?.chargeRateName ? `${item.chargeRateName}: ${reason}` : reason;
    });
};

export const SelectStaff = ({
    handleSelectStaff,
    disabled,
    typeError,
    currentStaff,
    clientStaffs,
    handleRemoveStaff,
    excludeId,
    required = true,
    helperText,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popper' : undefined;
    const loader = FindLoad(GET_CLIENT_STAFFS);
    const [search, setSearch] = useState('');
    const staffRef = useRef(null);
    const [list, setList] = useState([]);
    const [sortedData, setSortedDate] = useState([]);
    const ifNotEmpty = list?.filter((item) => item.staffs.filter((j) => !excludeId || j.id !== excludeId).length > 0);
    // An empty array is also the store's "not loaded" value, so only explain when charge rates came back without staff.
    const noEligibleStaff = !loader?.length && !!clientStaffs?.length && !clientStaffs.some((item) => item?.staffs?.length);
    const noStaffReasons = noEligibleStaff ? buildNoStaffReasons(clientStaffs) : [];

    const sortByChargeRatePriceDesc = (array) => {
        return array.sort((a, b) => b.chargeRatePrice - a.chargeRatePrice);
    };

    useEffect(() => {
        if (clientStaffs) {
            const sorted = sortByChargeRatePriceDesc(clientStaffs);
            setList(sorted);
            setSortedDate(sorted);
        }
    }, [clientStaffs]);

    const handleClick = (event) => {
        setSearch(null);
        staffRef.current.focus();
        if (!disabled) {
            setAnchorEl(anchorEl ? null : event.currentTarget);
        }
    };

    const handleSelect = (item) => {
        handleSelectStaff(item);
        setAnchorEl(null);
        setSearch(null);
    };

    const removeStaff = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleRemoveStaff();
        setAnchorEl(null);
        setSearch(null);
    };
    const handleFilter = (e) => {
        const searchValue = e.target.value.toLowerCase().trim();
        setSearch(e.target.value);

        if (searchValue) {
            const filteredList = sortedData
                .map((item) => {
                    const matchingStaffs = item.staffs.filter((staff) => {
                        const name = renderStaffName(staff)?.toLowerCase();
                        return name && name.includes(searchValue);
                    });
                    return matchingStaffs.length > 0 ? { ...item, staffs: matchingStaffs } : null;
                })
                .filter(Boolean);

            setList(filteredList);
        } else {
            setList(sortedData);
        }
    };

    const handleCloseAnchor = () => {
        setAnchorEl(null);
        setSearch(null);
        setList(sortedData);
    };

    const renderColorCircle = (index) => {
        if (list?.length >= 3) {
            if (index === 0) {
                return <div className="circle-box green" />;
            } else if (index === 1) {
                return <div className="circle-box yellow" />;
            } else {
                return <div className="circle-box red" />;
            }
        } else if (list?.length === 2) {
            if (index === 0) {
                return <div className="circle-box green" />;
            } else {
                return <div className="circle-box yellow" />;
            }
        } else {
            return <div className="circle-box green" />;
        }
    };

    return (
        <div className="staff-input-box">
            <div
                className={`staff-list-input`}
                onClick={handleClick}
                style={disabled ? { borderColor: 'rgba(75, 92, 104, 0.50)' } : typeError ? { borderColor: '#F07379' } : {}}
            >
                <p
                    className={anchorEl || currentStaff?.id || currentStaff?._id ? 'staff-input-selected' : 'staff-input-not-selected'}
                    style={{
                        transition: 'all .3s',
                        color: disabled
                            ? 'rgba(75, 92, 104, 0.50)'
                            : // anchorEl || currentStaff?.id || currentStaff?._id ? '#347AF0' :
                              'rgba(75, 92, 104 )',
                    }}
                >
                    {required ? 'Staff List*' : 'Staff List'}
                </p>
                <input
                    ref={staffRef}
                    disabled={disabled}
                    value={
                        search
                            ? search
                            : currentStaff
                              ? `${renderStaffName(currentStaff)} ${currentStaff?.employment ? `(${currentStaff.employment})` : ''}`
                              : // `${currentStaff?.firstName} ${currentStaff?.lastName}`
                                ''
                    }
                    onChange={handleFilter}
                />

                {loader?.length ? (
                    <InputMinLoader />
                ) : (
                    <div className="select-remove-icon">
                        {currentStaff && !disabled && (
                            <button className="remove-selected-staff" onClick={(e) => removeStaff(e)}>
                                <img src={Images.remove} alt="icon" />
                            </button>
                        )}
                        <img
                            className="input-rotate-icon"
                            style={anchorEl ? { transform: 'rotate(-180deg)' } : {}}
                            src={Images.dropdownArrowFilledBlue}
                            alt="icon"
                        />
                    </div>
                )}
            </div>
            {noEligibleStaff && !disabled && !typeError ? (
                <div className="no-eligible-staff-reason" style={{ fontSize: 12, color: '#F07379', marginTop: 4 }}>
                    {noStaffReasons?.map((reason, k) => (
                        <p key={k}>{reason}</p>
                    ))}
                </div>
            ) : helperText && !typeError ? (
                <p className="select-staff-helper">{helperText}</p>
            ) : (
                <ErrMessage text={typeError ? typeError : ' '} />
            )}
            <Popper
                id={id}
                open={open}
                anchorEl={anchorEl}
                className="custom-select-paper-box"
                placement="bottom-start"
                style={{ position: 'absolute', zIndex: 2600 }}
            >
                <div>
                    {list?.map((item, index) =>
                        item?.staffs
                            ?.filter((j) => !excludeId || j.id !== excludeId)
                            ?.map((j, k) => (
                            <div
                                key={k}
                                className="select-item-box"
                                onClick={() =>
                                    handleSelect({
                                        chargeRateId: item?.chargeRateId,
                                        payCodeType: item?.payCodeTypeId,
                                        payCodeTypeCode: item?.payCodeTypeCode,
                                        payCodeTypeName: item?.payCodeTypeName,
                                        ...j,
                                    })
                                }
                            >
                                {renderColorCircle(index)}
                                <TextRow name={`${renderStaffName(j)} ${j?.employment ? `(${j.employment})` : ''}`} />
                            </div>
                        ))
                    )}
                    {/* No eligible staff: the reason already shows under the input, so the list stays empty. */}
                    {ifNotEmpty?.length === 0 && !noEligibleStaff ? (
                        <div className="select-item-box">
                            {loader?.length ? (
                                <p>Loading...</p>
                            ) : (
                                <p>{search ? 'No staff matches your search' : 'No other eligible staff'}</p>
                            )}
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </Popper>

            {open && <div className="beck-drop" onClick={handleCloseAnchor} />}
        </div>
    );
};
